/// <amd-module name="Release/Landing/HubCommands" />
import { MenuItemType, IMenuItem } from "VSSUI/Menu";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { IVssFeatureService } from "VSS/Platform/Feature";

import { Release } from "Release/Client/Contracts/Release";
import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { editPipelineCommand, renamePipelineCommand, addRemoveFavoritesCommand, clonePipelineCommand, deletePipelineCommand, movePipelineCommand, exportPipelineCommand } from "Release/Commands/PipelineCommandsService";
import { newFolderAction } from "Release/Commands/CreateNewFolderService";
import { createReleaseCommand, createDratReleaseCommand } from "Release/Commands/CreateReleaseCommandService";
import { addPipelineToDashboardCommand } from "Release/Commands/AddReleasePipelineToDashboardCommandService";
import { pipelineSecurityCommand } from "Release/Commands/ShowSecurityCommandService";
import { renameFolderAction } from "Release/Commands/RenameFolderService";
import { deleteFolderCommand } from "Release/Commands/DeleteFolderCommandService";

import { ReleaseLandingCommandBarConstants } from "Constants";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { AllDefinitionsActionCreator } from "LeftPanel/AllPipelines/AllDefinitionsActionCreator";
import { PipelinesStore } from "LeftPanel/PipelinesStore";
import { PipelineSearchStore } from "Release/Landing/LeftPanel/PipelineSearch/PipelineSearchStore";
import { Folder } from "Release/Client/Contracts/Release";
import { ActiveReleasesActions } from "Release/Landing/HubContent/Releases/ActiveReleasesActions";
import { PipelinesActions } from "LeftPanel/PipelinesActions";
import { ActiveReleasesActionCreator } from "./HubContent/Releases/ActiveReleasesActionCreator";

export function getHubPipelineCommands(context: IReleaseDefinitionPivotCommandContext, isClassicPipelineCreationdDisabled: boolean = false): IObservableArray<IMenuItem> {
    const menuItems: IObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>();
    const featureService: IVssFeatureService = context.pageContext.getService<IVssFeatureService>("IVssFeatureService") || null;
    context.telemetrySource = TelemetrySource;

    let command: IHeaderCommandBarItem | undefined = editPipelineCommand(context) as IHeaderCommandBarItem;
    if (command) {
        command.important = true;
        menuItems.push(command);
    }

    const createReleaseCallback = (release: Release): void => {
        _getActiveReleasesActionCreator().handleCreatedRelease(context.pageContext, release);
    };
    command = createReleaseCommand(context, createReleaseCallback);
    if (command) {
        command.important = true;
        command.isPrimary = true;
        menuItems.push(command);
    }

    if (!!featureService && !featureService.isFeatureFlagEnabled("WebAccess.ReleaseManagement.HideCreateDraftRelease", false) && !isClassicPipelineCreationdDisabled) {
        command = createDratReleaseCommand(context);
        markAsUnimportantAndAdd(menuItems, command);
    }

    if (menuItems.length > 0) {
        menuItems.push({ id: "divider-1", important: false, itemType: MenuItemType.Divider });
    }

    command = pipelineSecurityCommand(context);
    markAsUnimportantAndAdd(menuItems, command)

    command = renamePipelineCommand(context, (newPipelineName: string) => {
        _getAllDefinitionsActionCreator().renamePipeline(context.pageContext, context.pipelineId as number, newPipelineName);
    });
    markAsUnimportantAndAdd(menuItems, command)

    command = movePipelineCommand(context, (newParentPath: string) => {
        _getAllDefinitionsActionCreator().movePipeline(context.pageContext, context.pipelineId as number, context.folderPath, newParentPath);
    });
    markAsUnimportantAndAdd(menuItems, command)

    if (isClassicPipelineCreationdDisabled){
        command = newFolderAction(context, (newFolderPath: string) => {
            ActionCreatorManager.GetActionCreator<AllDefinitionsActionCreator>(AllDefinitionsActionCreator).createFolder(context.pageContext, newFolderPath);
        });
        markAsUnimportantAndAdd(menuItems, command)    
    }

    if (menuItems.length > 0 && menuItems.value[menuItems.length - 1].itemType !== MenuItemType.Divider) {
        menuItems.push({ id: "divider-2", important: false, itemType: MenuItemType.Divider });
    }

    const favoriteCommandPosition = menuItems.length;
    const favortieCommandPromise = addRemoveFavoritesCommand(context);
    if (favortieCommandPromise) {
        favortieCommandPromise.then((favCommand: IHeaderCommandBarItem | undefined) => {
            if (favCommand) {
                favCommand.important = false;
                menuItems.splice(favoriteCommandPosition, 0, favCommand);
            }
        });
    }

    command = addPipelineToDashboardCommand(context);
    markAsUnimportantAndAdd(menuItems, command)

    if (menuItems.length > 0 && menuItems.value[menuItems.length - 1].itemType !== MenuItemType.Divider) {
        menuItems.push({ id: "divider-3", itemType: MenuItemType.Divider, important: false });
    }

    if (!isClassicPipelineCreationdDisabled) {
        command = clonePipelineCommand(context);
        markAsUnimportantAndAdd(menuItems, command)
    }
    
    command = exportPipelineCommand(context);
    markAsUnimportantAndAdd(menuItems, command)

    command = deletePipelineCommand(context);
    markAsUnimportantAndAdd(menuItems, command)

    if (menuItems.length > 0 && menuItems.value[menuItems.length - 1].itemType === MenuItemType.Divider) {
        menuItems.pop();
    }

    return menuItems;
}

export function getHubFolderCommands(context: IReleaseDefinitionPivotCommandContext): IObservableArray<IMenuItem> {
    const menuItems: IObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>();

    context.telemetrySource = TelemetrySource;
    let command;

    const showSearchResults: boolean = !!(_getPipelineSearchStore().getState().searchText);
    if (!showSearchResults) {
        // Do not show Rename / delete folder in search view
        command = renameFolderAction(context, (path: string) => {
            const exisitngFolder = _getPipelinesStore().getFolder(context.folderPath);
            if (exisitngFolder) {
                const newFolder: Folder = { ...exisitngFolder, path: path };
                _getAllDefinitionsActionCreator().renameFolder(context.pageContext, newFolder, exisitngFolder.path);
            }
        }, ReleaseLandingCommandBarConstants.moreButtonId);

        if (command) {
            menuItems.push(command);
        }

        command = deleteFolderCommand(context, (path) => {
            _getPipelinesActions().removeFolder.invoke(path);
        });
        if (command) {
            menuItems.push(command);
        }
    }

    command = pipelineSecurityCommand(context);
    if (command) {
        (command as IHeaderCommandBarItem).important = true;
        if (menuItems.length > 0) {
            menuItems.push({ id: "divider-1", itemType: MenuItemType.Divider, important: false });
        }

        menuItems.push(command);
    }

    return menuItems;
}

function markAsUnimportantAndAdd(menuItems: IObservableArray<IHeaderCommandBarItem>, incomingCommand: IHeaderCommandBarItem | undefined) {
    if (incomingCommand) {
        incomingCommand.important = false;
        menuItems.push(incomingCommand);
    }
}

function _getActiveReleasesActionCreator(): ActiveReleasesActionCreator {
    if (!_activeReleasesActionCreator) {
        _activeReleasesActionCreator = ActionCreatorManager.GetActionCreator<ActiveReleasesActionCreator>(ActiveReleasesActionCreator);
    }

    return _activeReleasesActionCreator;
}

function _getAllDefinitionsActionCreator(): AllDefinitionsActionCreator {
    if (!_allDefinitionsActionCreator) {
        _allDefinitionsActionCreator = ActionCreatorManager.GetActionCreator<AllDefinitionsActionCreator>(AllDefinitionsActionCreator);
    }

    return _allDefinitionsActionCreator;
}

function _getPipelinesActions(): PipelinesActions {
    if (!_pipelinesActions) {
        _pipelinesActions = ActionsHubManager.GetActionsHub<PipelinesActions>(PipelinesActions);
    }

    return _pipelinesActions;
}

function _getPipelinesStore(): PipelinesStore {
    if (!_pipelinesStore) {
        _pipelinesStore = StoreManager.GetStore<PipelinesStore>(PipelinesStore);
    }

    return _pipelinesStore;
}

function _getPipelineSearchStore(): PipelineSearchStore {
    if (!_pipelinesStore) {
        _pipelineSearchStore = StoreManager.GetStore<PipelineSearchStore>(PipelineSearchStore);
    }

    return _pipelineSearchStore;
}

let _allDefinitionsActionCreator: AllDefinitionsActionCreator;
let _activeReleasesActions: ActiveReleasesActions;
let _activeReleasesActionCreator: ActiveReleasesActionCreator;
let _pipelinesActions: PipelinesActions;
let _pipelinesStore: PipelinesStore;
let _pipelineSearchStore: PipelineSearchStore;
const TelemetrySource = "ReleaseLWPLanding";
